import { useEffect } from 'react';
import { VStack } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';

import { useVerifyTokenQuery } from '../../../services';
import { CenteredSpinner } from '../../../components';
// Canonical envelope helpers — see src/utils/api-response.ts.
import {
  MutationResult,
  classifyMutationResult,
} from '../../../utils/api-response';

// This page is the gate in front of activation: it hands the invitation token
// to /new-password only if the backend says the token is good. It used to
// match ONE literal message — `data?.message === 'INVALID TOKEN'` — so every
// other rejection (a different message, a new error code, an empty body) fell
// through to the advance branch and handed a dead token to the next screen,
// where the user hit a wall with no way back. It now FAILS CLOSED: only a
// body-level success advances; anything else goes to /invalid-invitation,
// which is a real screen with a way out rather than an endless spinner.
export const NewAccount = () => {
  const navigate = useNavigate();
  const { token } = useParams();

  const {
    data,
    isSuccess: isQueryAnswered,
    isError: isQueryFailed,
  } = useVerifyTokenQuery(String(token));

  // `data` from a query hook IS the AppResponse envelope, so wrap it once into
  // the shape the shared classifier reads. Both values below are primitives,
  // so the effect cannot re-fire on a new result-object identity.
  const isSettled = isQueryAnswered || isQueryFailed;
  const outcome = isQueryFailed
    ? MutationResult.TRANSPORT_FAILURE
    : classifyMutationResult({ data });

  useEffect(() => {
    // Still in flight: keep the spinner, decide nothing.
    if (!isSettled) return;

    if (outcome === MutationResult.SUCCESS) {
      navigate('/new-password', {
        state: { activationToken: token },
      });
      return;
    }

    // Every non-success lands here — a rejected body regardless of its message
    // text, and a transport failure, which leaves the token just as unusable.
    navigate('/invalid-invitation');
  }, [isSettled, outcome, navigate, token]);

  return (
    <VStack h="full">
      <CenteredSpinner />
    </VStack>
  );
};
